import { Icon, IconIdentifier } from '@aus-platform/design-system';
import { isEmpty } from 'lodash';
import React, { useEffect, useMemo } from 'react';

// Types.
type PasswordValidationMessageProps = {
  password: string;
  setIsPasswordValidationError: (value: boolean) => void;
};

type ValidationItem = {
  key: string;
  label: string;
};

// Enums.
enum ValidationState {
  Idle = 'idle',
  Success = 'success',
  Failure = 'failure',
}

// Constants.
const validationItems: ValidationItem[] = [
  { key: 'minLength', label: 'Minimum 8 characters' },
  { key: 'hasUpper', label: 'At least 1 uppercase letter' },
  { key: 'hasLower', label: 'At least 1 lowercase letter' },
  { key: 'hasNumber', label: 'At least 1 number' },
  { key: 'hasSpecial', label: 'At least 1 special character' },
];

export const PasswordValidationMessage: React.FC<
  PasswordValidationMessageProps
> = ({ password, setIsPasswordValidationError }) => {
  // useMemo.
  const validationResults = useMemo(() => {
    return {
      minLength: password.length >= 8,
      hasUpper: /[A-Z]/.test(password),
      hasLower: /[a-z]/.test(password),
      hasNumber: /[0-9]/.test(password),
      hasSpecial: /[!@#$%^&*]/.test(password),
    };
  }, [password]);

  // useEffect.
  useEffect(() => {
    if (isEmpty(password)) {
      setIsPasswordValidationError(true);
      return;
    }

    const isValid = Object.values(validationResults).every(Boolean);
    setIsPasswordValidationError(!isValid);
  }, [validationResults, password]);

  // Handlers.
  const getCurrentStatus = (isFulfilled: boolean) => {
    if (isEmpty(password)) {
      return {
        icon: IconIdentifier.CheckCircleFill,
        labelColor: ValidationState.Idle,
      };
    }

    return isFulfilled
      ? {
          icon: IconIdentifier.CheckCircleFill,
          labelColor: ValidationState.Success,
        }
      : {
          icon: IconIdentifier.CloseCircle,
          labelColor: ValidationState.Failure,
        };
  };

  // Renders.
  const renderValidationItem = (label: string, isFulfilled: boolean) => {
    const { icon, labelColor } = getCurrentStatus(isFulfilled);

    return (
      <li
        className={`validation-list-item validation-list-item__${labelColor}`}
        key={label}
      >
        <Icon identifier={icon} size={16} />
        <span>{label}</span>
      </li>
    );
  };

  return (
    <ul className="password-validation-message-container">
      {validationItems.map(({ label, key }) =>
        renderValidationItem(label, validationResults[key]),
      )}
    </ul>
  );
};
